import React from 'react';
import { uniq } from 'lodash';
import { Divider, Grid, Typography } from '@mui/material';
import { VersionGroupedByParent } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { getVersionFromGroup } from '@/_utils/bulkSearchUtils';

interface OPVersionSelectionsFlatProps {
  selectedVersions: VersionGroupedByParent;
}

const OPVersionSelectionsFlatFromLineage = ({ selectedVersions }: OPVersionSelectionsFlatProps) => {
  const versionKeys = Object.keys(selectedVersions);

  const renderVersionsListAsText = (versions: VersionGroupedByParent, parentKey: string) => {
    // Check if this is actually a version object with feature parent
    if (versions && typeof versions === 'object') {
      // If the versions object has properties that suggest it's a single version object
      const hasVersionProperties = 'entity_type' in versions || 'lineage_meta' in versions || 'version_type' in versions;

      if (hasVersionProperties) {
        return null;
      }
    }

    const newSortedVersions = uniq(
      Object.keys(versions)
        .filter((key) => key.startsWith('_')) // Only process version IDs
        .map((version) => {
          const versionObj = Object.values(selectedVersions[parentKey][version]).pop() as TitleEntityV2 | undefined;

          return versionObj?.lineage_meta?.episode_sequence_order ?? '?';
        })
    ).sort((a, b) => a - b);

    // for creating the sequence range
    const ranges: string[] = [];
    let start, end;

    // Filter out non-numeric values before processing ranges
    const numericVersions = newSortedVersions.filter((v) => !isNaN(Number(v)) && Number(v) !== null && Number(v) !== undefined);

    for (let i = 0; i < numericVersions.length; i++) {
      start = Number(numericVersions[i]);
      end = start;
      while (i + 1 < numericVersions.length && Number(numericVersions[i + 1]) - Number(numericVersions[i]) == 1) {
        end = Number(numericVersions[i + 1]); // increment the index if the numbers sequential
        i++;
      }
      ranges.push(start == end ? start + '' : start + ' - ' + end);
    }

    // converting the range array to a string
    const versionsString =
      ranges.length > 0
        ? ranges.map((range) => `E${range}`).join(', ')
        : newSortedVersions
            .filter((v) => v !== null && v !== undefined)
            .map((v) => `E${v}`)
            .join(', '); // fallback for non-numeric episodes
    return <Typography sx={{ opacity: 0.6 }}>{versionsString}</Typography>;
  };

  return versionKeys
    .sort((a, b) => {
      const versionASequence = getVersionFromGroup(selectedVersions, a)?.lineage_meta?.season_sequence_order || 0;
      const versionBSequence = getVersionFromGroup(selectedVersions, b)?.lineage_meta?.season_sequence_order || 0;
      return versionASequence - versionBSequence;
    })
    .map((versionKey, index) => {
      const version = getVersionFromGroup(selectedVersions, versionKey);
      const versionText = renderVersionsListAsText(selectedVersions[versionKey], versionKey);

      return (
        <Grid container key={versionKey} flexDirection="column">
          <Grid container alignItems="center">
            <Typography>{version?.entity_type === 'version' ? version?.lineage_meta?.season_name : '-'}</Typography>
            <Typography sx={{ mx: 1, opacity: 0.6 }}>{'>'}</Typography>
            {versionText}
          </Grid>
          {index !== versionKeys.length - 1 && (
            <Grid item>
              <Divider sx={{ my: 1, borderColor: 'action.selected', borderStyle: 'dashed' }} />
            </Grid>
          )}
        </Grid>
      );
    });
};

export default OPVersionSelectionsFlatFromLineage;
